// Solana wallet bridge — Wallet Standard (the only thing that works
// reliably in TWA / Chrome custom tabs).
//
// MWA is registered at app boot in `main.ts` via `registerMwa(...)`. After
// that, the MWA wallet appears in `getWallets().get()` alongside any
// browser-injected wallets (Phantom extension, Solflare). We discover the
// best wallet, call its `standard:connect` to get an account, and hand the
// resulting `Wallet + WalletAccount` to `solana.ts` for signing.

import { getWallets } from '@wallet-standard/app';
import type { Wallet, WalletAccount } from '@wallet-standard/base';
import { mwaReady } from './mwa';

type SolanaAddress = string;

export type WalletKind = 'standard';   // legacy enum kept for old call sites
export type ConnectedWalletKind = WalletKind;

let _address: SolanaAddress | null = null;
let _wallet: Wallet | null = null;
let _account: WalletAccount | null = null;
const listeners = new Set<(addr: SolanaAddress | null) => void>();

export function walletAddress(): SolanaAddress | null { return _address; }
export function connectedWalletKind(): ConnectedWalletKind | null { return _address ? 'standard' : null; }
export function injectedProvider(): any { return null; }   // legacy stub
export function walletClient(): null { return null; }
export function publicClient(): null { return null; }
export function currentNetwork(): 'mainnet' { return 'mainnet'; }
export function setNetwork(_: string): void {}

/** The connected Wallet Standard wallet — `solana.ts` needs it for signing. */
export function connectedWallet(): Wallet | null { return _wallet; }
export function connectedAccount(): WalletAccount | null { return _account; }

export function onAddressChange(fn: (addr: SolanaAddress | null) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function emit() { for (const fn of listeners) fn(_address); }

// ── Wallet discovery ────────────────────────────────────────────────────

/**
 * Find the best Solana wallet currently registered with the Wallet Standard.
 * Priority:
 *   1. Mobile Wallet Adapter (registered by `registerMwa` in main.ts) —
 *      this is what works in TWA / Chrome custom tabs on Android.
 *   2. Any other wallet that advertises a `solana:` chain (Phantom extension
 *      on desktop, Backpack, Solflare).
 *
 * Returns null if no Solana wallet is registered (cold desktop, no extension).
 */
function pickSolanaWallet(): Wallet | null {
  const wallets = getWallets().get();
  const mwa = wallets.find((w) => w.name === 'Mobile Wallet Adapter');
  if (mwa) return mwa;
  return wallets.find((w) => w.chains.some((c) => c.startsWith('solana:'))) ?? null;
}

/**
 * Wait until a Solana wallet (preferably MWA on Seeker) is registered. Used
 * by `connect()` to close the race window where the user taps CONNECT
 * before `registerMwa()` has finished. We:
 *   1. Await the static `mwaReady` promise — guarantees `registerMwa` was
 *      at least invoked. On Seeker this is usually enough.
 *   2. Subscribe to `getWallets().on('register', …)` and resolve when the
 *      MWA wallet (or any other Solana wallet) actually shows up in the
 *      registry. Some wallets register on a microtask AFTER registerMwa
 *      returns; this catches that.
 *   3. Cap the wait at `timeoutMs` so we don't hang forever on a desktop
 *      where no Solana wallet will ever be installed.
 */
async function awaitSolanaWallet(timeoutMs = 1500): Promise<Wallet | null> {
  // Step 1 — let the static MWA registration settle.
  try { await mwaReady; } catch { /* */ }
  let found = pickSolanaWallet();
  if (found) return found;

  // Step 2 — listen for `register` events from any wallet that arrives
  // late (some inject themselves on a setTimeout after page load).
  return new Promise<Wallet | null>((resolve) => {
    let done = false;
    const wallets = getWallets();
    const off = wallets.on('register', () => {
      if (done) return;
      const f = pickSolanaWallet();
      if (f) { done = true; off(); resolve(f); }
    });
    setTimeout(() => {
      if (done) return;
      done = true;
      off();
      resolve(pickSolanaWallet());
    }, timeoutMs);
  });
}

async function publicKeyToBase58(raw: Uint8Array): Promise<string> {
  const { PublicKey } = await import('@solana/web3.js');
  return new PublicKey(raw).toBase58();
}

// ── Connect / disconnect ───────────────────────────────────────────────

export async function connect(): Promise<SolanaAddress | null> {
  // Race-safe wallet discovery — see `awaitSolanaWallet`. On Seeker this
  // returns immediately because MWA is registered synchronously at module
  // load; on cold desktop it gives any late-binding extension up to 1.5 s
  // to declare itself before we surrender.
  const w = await awaitSolanaWallet();
  if (!w) {
    // Common Seeker case: the user has Seed Vault enabled but no
    // MWA-capable wallet app (Phantom mobile, Backpack, Solflare)
    // installed. Direct them to install one.
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    const isSeekerish = /android/i.test(ua);
    alert(isSeekerish
      ? 'No Solana wallet detected.\n\nOn Seeker / Android, install Phantom, Solflare or Backpack from the dApp Store, sign in once, then return and tap CONNECT WALLET.'
      : 'No Solana wallet detected. Install Phantom or Backpack browser extension (desktop), or use Phantom mobile on a phone.');
    return null;
  }
  const features = w.features as any;
  const connectFeature = features['standard:connect'];
  if (!connectFeature?.connect) {
    alert(`Wallet "${w.name}" does not implement standard:connect — it cannot be used here. Try a different wallet.`);
    throw new Error(`Wallet "${w.name}" does not implement standard:connect`);
  }
  let result;
  try {
    result = await connectFeature.connect();
  } catch (e: any) {
    // The native MWA flow can fail for many reasons: user rejected,
    // Seed Vault is locked, the wallet app crashed, no network, etc.
    // Surface the real error so the user knows what to do next instead
    // of silently restoring the CONNECT WALLET label.
    const msg = String(e?.message ?? e).slice(0, 240);
    console.warn('[wallet] standard:connect threw', e);
    alert(`Wallet connect failed:\n\n${msg}\n\nTap CONNECT WALLET again to retry.`);
    throw e;
  }
  const acct = result?.accounts?.[0];
  if (!acct) {
    alert(`Wallet "${w.name}" returned no accounts. Authorize the app inside the wallet sheet and try again.`);
    return null;
  }
  _wallet = w;
  _account = acct;
  _address = await publicKeyToBase58(acct.publicKey);
  persistSession({ address: _address, kind: 'standard' });
  emit();
  return _address;
}

export async function disconnect() {
  try {
    const features = _wallet?.features as any;
    await features?.['standard:disconnect']?.disconnect?.();
  } catch { /* */ }
  _address = null;
  _wallet = null;
  _account = null;
  persistSession(null);
  emit();
}

// ── Session persistence ────────────────────────────────────────────────

const STORAGE_KEY = 'cosmic-seeker.wallet';
interface PersistedSession { address: string; kind: WalletKind; ts: number; }

function persistSession(s: { address: string; kind: WalletKind } | null) {
  try {
    if (!s) { localStorage.removeItem(STORAGE_KEY); return; }
    const blob: PersistedSession = { ...s, ts: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(blob));
  } catch { /* */ }
}

function readSession(): PersistedSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const blob = JSON.parse(raw) as PersistedSession;
    if (!blob.address) return null;
    if (Date.now() - (blob.ts ?? 0) > 30 * 86_400_000) return null;
    return blob;
  } catch { return null; }
}

/**
 * Restore a saved session at app boot. We show the cached address
 * immediately so the HUD doesn't flicker through "disconnected", then
 * trigger a silent reconnect when a wallet feature exposes one
 * (`standard:connect` with `{silent:true}` is supported by some wallets;
 * MWA cached auth handles the rest on next signing call).
 */
export async function restoreSession(): Promise<SolanaAddress | null> {
  const cached = readSession();
  if (!cached) return null;
  _address = cached.address;
  emit();
  // Try to silently rebind the wallet so the next sign call doesn't
  // require a re-pick. We give wallet-standard a tick to finish
  // registering (registerMwa runs async in main.ts).
  setTimeout(() => { void silentReconnect(); }, 200);
  return _address;
}

async function silentReconnect() {
  const w = await awaitSolanaWallet();
  if (!w) return;
  const features = w.features as any;
  // Reuse cached MWA authorization if available — calling `standard:connect`
  // with `silent:true` returns without showing UI when the wallet has a
  // valid cached session.
  try {
    const r = await features['standard:connect']?.connect?.({ silent: true });
    const acct = r?.accounts?.[0];
    if (acct) {
      _wallet = w;
      _account = acct;
      _address = await publicKeyToBase58(acct.publicKey);
      persistSession({ address: _address, kind: 'standard' });
      emit();
    }
  } catch (e) {
    console.warn('[wallet] silent reconnect failed:', e);
  }
}
